import { cloneDeep, isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { JSX, useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { track } from '~/src/dash/analytics/analytics';

import {
  canFetchForecastingData,
  getDefaultDeliveryVariables,
  defaultForecastingData,
  EGroupByType,
  fetchForecastingData,
  getActiveInventoryParameters,
  getDefaultInventoryParameters,
  IDeliveryVariables,
  IInventoryParameters,
  validateForecast,
} from '~/src/dash/apiV2/forecasting/forecasting';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  borderRadius,
  ButtonV2,
  Col,
  Cols,
  Panel,
  Panels,
  Row,
  Rows,
  spacing,
  Span,
  Text,
  TooltipWithTranslation,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { useInterval } from '~/src/dash/utils/hooks';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import { DeliveryVariables } from '~/src/dash/views/tools/Forecasting/DeliveryVariables';
import { Forecast } from '~/src/dash/views/tools/Forecasting/Forecast';
import { InventoryParameters } from '~/src/dash/views/tools/Forecasting/InventoryParameters';
import { TopBar } from '~/src/dash/views/topbar/TopBar';
import { InventoryParametersParamKey } from '~/src/dash/views/tools/Forecasting/InventoryParameters/config';

export const ForecastingView = (): JSX.Element => {
  const { session, user, isSuperUser, location } = useBaseStore();
  const { t } = usePublicaTranslation();

  const { Tools } = useBaseConfig();
  const cfg = Tools.Forecasting.Main.InventoryParameters;
  const [inventoryParameters, setInventoryParameters] = useState(
    getDefaultInventoryParameters(session.publisher.Timezone),
  );
  const [deliveryVariables, setDeliveryVariables] = useState(
    getDefaultDeliveryVariables(user.publisher.Timezone),
  );
  const [forecastingData, setForecastingData] = useState(defaultForecastingData);
  const [listsLoadState, setListsLoadState] = useState(LoadingStateLoading);
  const [inventoryParametersLists, setInventoryParametersLists] = useState<
    Record<string, ISelectOption[]>
  >({});
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  const [forecastingDataLoadState, setForecastingDataLoadState] = useState(LoadingStateNoData);
  const [groupByType, setGroupByType] = useState(EGroupByType.Tier);

  // saves into state at the time forecast, to be used for accurately generating csv
  const [inventoryParametersSnapshot, setInventoryParametersSnapshot] = useState(
    {} as IInventoryParameters,
  );
  const [deliveryVariablesSnapshot, setDeliveryVariablesSnapshot] = useState(
    {} as IDeliveryVariables,
  );
  const [canForecast, setCanForecast] = useState(false);

  useEffect(() => {
    populateDropdowns();

    // Check forecast availability immediately on load
    canFetchForecastingData(session.publisher.ID)
      .then((result) => {
        setCanForecast(result);
      })
      .catch(() => {
        setCanForecast(false);
      });
  }, [location.pathname]);

  const populateDropdowns = () => {
    setListsLoadState(LoadingStateLoading);
    const keys: string[] = [];
    const getListFns: Array<Promise<ISelectOption[]>> = [];
    Object.values(cfg.dropdowns).forEach((dropdown) => {
      keys.push(dropdown.key);
      getListFns.push(dropdown.getList(session, user));
    });
    const tempLists: Record<string, ISelectOption[]> = {};
    Promise.all(getListFns)
      .then((results) => {
        results.forEach((result, i) => {
          tempLists[keys[i]] = result;
        });
        unstable_batchedUpdates(() => {
          setInventoryParametersLists(tempLists);
          setListsLoadState(!isEmpty(tempLists) ? LoadingStateHasData : LoadingStateNoData);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setInventoryParametersLists({});
          setListsLoadState(LoadingStateNoData);
          setSnackbarError(true);
          setSnackbarMessage(t('ErrorLoadingData'));
        });
      });
  };

  useInterval(() => {
    canFetchForecastingData(session.publisher.ID)
      .then((result) => {
        setCanForecast(result);
      })
      .catch(() => {
        setCanForecast(false);
      });
  }, 60000); // Check every minute

  const formatInventoryParameters = (): IInventoryParameters => {
    const newInventoryParameters = { ...inventoryParameters } as IInventoryParameters;
    const inventoryParametersKeys = Object.values(
      inventoryParameters.ActiveParameters,
    ).flat() as string[];
    const errors = {} as Record<InventoryParametersParamKey, string[]>;

    // get active parameters
    (inventoryParametersKeys as InventoryParametersParamKey[]).forEach(
      (paramKey: InventoryParametersParamKey) => {
        const parameterInfo = cfg.params[paramKey];

        if (!isEmpty(parameterInfo.customUIConversions)) {
          const { apiKey, uiKey, convertPayload } = parameterInfo.customUIConversions;
          const convertedResult = convertPayload(
            inventoryParameters[uiKey as keyof IInventoryParameters],
          );
          // @ts-expect-error - Dynamic property assignment from config
          newInventoryParameters[apiKey] = convertedResult.value as unknown;
          if (!isEmpty(convertedResult.errors)) {
            errors[paramKey] = convertedResult.errors;
          }
        }

        if (!isEmpty(parameterInfo.apiKeys)) {
          parameterInfo.apiKeys?.forEach((apiKey) => {
            // @ts-expect-error - Dynamic property copy from config
            newInventoryParameters[apiKey] = newInventoryParameters[apiKey];
          });
        }

        if (!isEmpty(errors)) {
          unstable_batchedUpdates(() => {
            setSnackbarMessage(t('ErrorPleaseResolveAllErrors'));
            setSnackbarError(true);
            setForecastingDataLoadState(LoadingStateNoData);
          });
          return;
        }
      },
    );
    return newInventoryParameters;
  };

  const handleRunForecast = (groupByType: EGroupByType) => {
    const startTime = moment().unix();
    // Validation
    const validationErr = validateForecast(deliveryVariables);
    if (validationErr !== '') {
      unstable_batchedUpdates(() => {
        setSnackbarError(true);
        setSnackbarMessage(validationErr);
      });
      return;
    }
    setForecastingDataLoadState(LoadingStateLoading);
    setGroupByType(groupByType);
    // MixPanel Tracking
    track('Forecast - Run Forecast', { isSuperUser }, user.user);
    track(
      'Forecast - Selected Params',
      { isSuperUser, inventory_parameters: getActiveInventoryParameters(inventoryParameters) },
      user.user,
    );
    if (deliveryVariables.FreqCapActive) {
      track('Forecast - Active Frequency Cap', { isSuperUser }, user.user);
    }
    if (deliveryVariables.ExcludeHouseCampaigns) {
      track('Forecast - Active Exclude House Campaigns', { isSuperUser }, user.user);
    }
    if (deliveryVariables.TrafficAdjustment > 0) {
      track('Forecast - Adjustment to Traffic', { isSuperUser }, user.user);
    }
    fetchForecastingData(
      session.publisher.ID,
      formatInventoryParameters(),
      deliveryVariables,
      groupByType,
      user.publisher.Timezone,
    )
      .then((resp) => {
        unstable_batchedUpdates(() => {
          setForecastingData(resp);
          setForecastingDataLoadState(LoadingStateHasData);
          setInventoryParametersSnapshot(cloneDeep(inventoryParameters));
          setDeliveryVariablesSnapshot(deliveryVariables);
        });
        track(
          'Forecast - Complete Display',
          { isSuperUser, load_duration_sec: moment().unix() - startTime },
          user.user,
        );
      })
      .catch((err) => {
        console.error(err);
        setForecastingDataLoadState(LoadingStateNoData);
      });
  };

  // this formats the calendars to Mon-Sun and resets it back to Sun-Sat on unmount
  useEffect(() => {
    moment.updateLocale('en', {
      week: {
        dow: 1,
      },
    });
    moment.weekdays(true);
    return () => {
      moment.updateLocale('en', {
        week: {
          dow: 0,
        },
      });
      moment.weekdays(false);
    };
  }, []);

  return (
    <Panels max vertical relative minWidthMain>
      <Panel largeHorizontalPadding smallVerticalPadding>
        <TopBar title='Tools' enableUINotices />
      </Panel>
      <Panel
        largeHorizontalPadding
        smallVerticalPadding
        style={{
          borderBottom: `1px solid ${publicaColors.lightGray}`,
        }}
      >
        {/* TITLE - SAVE RUN FORECAST */}
        <Cols gap={spacing.base} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Col flex>
            <Cols style={{ alignItems: 'center' }}>
              <Col displayFlex>
                <Text bold large>
                  Forecast
                </Text>
              </Col>
            </Cols>
          </Col>
          <Col>
            <Cols gap={spacing.small}>
              {/* uncomment when saving is implemented */}
              {/* <Col>
                <ButtonV2
                  small
                  onClick={function (): void {
                    throw new Error('Function not implemented.');
                  }}
                >
                  Save current setup
                </ButtonV2>
              </Col> */}
              <Col>
                <TooltipWithTranslation
                  tKey={!canForecast ? 'TooltipForecastIsTemporarilyUnavailable' : undefined}
                >
                  <div>
                    <ButtonV2
                      small
                      onClick={() => handleRunForecast(EGroupByType.Tier)}
                      disabled={!canForecast || forecastingDataLoadState === LoadingStateLoading}
                    >
                      <Cols
                        gap={spacing.extraSmall}
                        style={{
                          alignItems: 'center',
                        }}
                      >
                        <Col displayFlex>
                          <IconV2 name='shake' color={publicaColors.white} />
                        </Col>
                        <Col>
                          <Text tKey='RunForecast' small inheritColor inheritWeight />
                        </Col>
                      </Cols>
                    </ButtonV2>
                  </div>
                </TooltipWithTranslation>
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Panel>
      <Panel scroll paddingBottom='48px'>
        <Panels max vertical relative minWidthMain>
          <Panel verticalPadding largeHorizontalPadding>
            {/* NOTES */}
            <Rows>
              <Row
                smallPadding
                style={{
                  backgroundColor: publicaColors.lighterBlue,
                  width: 'fit-content',
                  borderRadius: borderRadius.base,
                }}
              >
                <Cols gap={spacing.extraSmall}>
                  <Col>
                    <IconV2
                      name='loader'
                      style={{
                        paddingTop: '4px',
                      }}
                    />
                  </Col>
                  <Col>
                    <Text small color={publicaColors.black}>
                      Bundle any aspect of your inventory to estimate future impressions available.
                      Use variables to adjust delivery settings, customize results on the fly.
                      Predictions are based on <Span heavy>historical data.</Span>
                    </Text>
                  </Col>
                </Cols>
              </Row>
            </Rows>
          </Panel>
          <Panel largeHorizontalPadding paddingBottom={spacing.large}>
            <InventoryParameters
              inventoryParameters={inventoryParameters}
              inventoryParametersLists={inventoryParametersLists}
              isLoaded={listsLoadState !== LoadingStateLoading}
            />
          </Panel>
          <Panel largeHorizontalPadding paddingBottom={spacing.large}>
            <DeliveryVariables
              deliveryVariables={deliveryVariables}
              setDeliveryVariables={setDeliveryVariables}
            />
          </Panel>
          <Panel largeHorizontalPadding paddingBottom={spacing.large}>
            <Forecast
              forecastingData={forecastingData}
              totalRows={forecastingData.ForecastingData.length}
              isActionPending={false}
              forecastingDataLoadState={forecastingDataLoadState}
              setForecastingDataLoadState={setForecastingDataLoadState}
              inventoryParametersSnapshot={inventoryParametersSnapshot}
              deliveryVariables={deliveryVariables}
              deliveryVariablesSnapshot={deliveryVariablesSnapshot}
              handleRunForecast={handleRunForecast}
              groupByType={groupByType}
            />
          </Panel>
        </Panels>
      </Panel>
      <SnackbarView
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={() => setSnackbarMessage('')}
      >
        {snackbarMessage}
      </SnackbarView>
    </Panels>
  );
};
